import Swiper from 'swiper';
class YourRoutineSlider extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.getAttribute('data-section-id');
    this.elements = {
      slides: this.querySelectorAll('.swiper-slide'),
      videos: this.querySelectorAll('video'),
      pagination: this.querySelector('.swiper-pagination'),
      nextSlide: this.querySelector('.swiper-button-next'),
      prevSlide: this.querySelector('.swiper-button-prev'),
    };
    this.swiperEl = this.querySelector('.swiper-container');
    this.swiperParams = {
      slidesPerView: 1,
      centeredSlides: true,
      centerInsufficientSlides: true,
      spaceBetween: 20,
      loop: true,
      autoHeight: false,
      a11y: {
        enable: true,
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide',
        firstSlideMessage: 'This is the first slide',
        lastSlideMessage: 'This is the last slide'
      },
      breakpoints: {
        320: {
          slidesPerView: 1.2
        },
        640: {
          slidesPerView: 1.2
        },
        768: {
          slidesPerView: 1.2,
          navigation: false
        },
        1024: {
          slidesPerView: 1,
          pagination: false
        }
      },
      pagination: {
        el: this.elements.pagination,
        clickable: true,
        bulletActiveClass: 'swiper-pagination-bullet-active',
        bulletClass: 'swiper-pagination-bullet',
        renderBullet: function (index, className) {
          return `<span class="${className}" role="button" aria-label="Go to slide ${index + 1}"></span>`;
        }
      },
      navigation: {
        nextEl: this.elements.nextSlide,
        prevEl: this.elements.prevSlide,
      }
    };
  }

  initSwiperSlider() {
    if (!this.swiperEl || this.swiper) return;

    // Initialize Swiper with modules and configuration
    this.swiper = new Swiper(this.swiperEl, {
      
      // Configure navigation
      navigation: {
        nextEl: this.elements.nextSlide,
        prevEl: this.elements.prevSlide,
        disabledClass: 'swiper-button-disabled',
        hideOnClick: false
      },
      
      // Configure pagination
      pagination: {
        el: this.elements.pagination,
        clickable: true,
        bulletClass: 'swiper-pagination-bullet',
        bulletActiveClass: 'swiper-pagination-bullet-active',
        renderBullet: function (index, className) {
          return `<span class="${className}" role="button" aria-label="Go to slide ${index + 1}"></span>`;
        },
        horizontalClass: 'swiper-pagination-horizontal'
      },
      
      // Basic parameters
      slidesPerView: 1.2,
      spaceBetween: 16,
      speed: 300,
      loop: false,
      watchOverflow: true,
      
      // Responsive breakpoints
      breakpoints: {
        320: {
          slidesPerView: 1.2,
          spaceBetween: 16
        },
        1024: {
          slidesPerView: 1,
          spaceBetween: 0
        }
      },
      
      // Callbacks
      on: {
        init: () => {
          this.handleSlideChange(0);
          // Update navigation buttons state on init
          if (this.swiper) {
            this.swiper.navigation.update();
          }
        },
        slideChange: (swiper) => {
          this.handleSlideChange(swiper.activeIndex);
        },
        resize: () => {
          // Update navigation buttons on resize
          if (this.swiper) {
            this.swiper.update();
          }
        }
      }
    });
  }

  handleSlideChange(activeIndex) {
    // Pause all videos
    if (this.elements.videos) {
      this.elements.videos.forEach(video => {
        video.pause();
      });
    }
    
    // Get active slide
    if (!this.swiper || !this.swiper.slides) return;
    
    const activeSlide = this.swiper.slides[activeIndex];
    if (!activeSlide) return;
    
    // Announce to screen readers which slide is active
    const slideTitle = activeSlide.querySelector('.product-title')?.textContent || `Slide ${activeIndex + 1}`;
    this.announceToScreenReader(`Now viewing: ${slideTitle}`);
    
    // Handle active video
    const activeVideo = activeSlide.querySelector('video');
    if (activeVideo) {
      // Reset video to beginning
      activeVideo.currentTime = 0;
      
      // Check if video has autoplay attribute and play it if it does
      if (activeVideo.hasAttribute('autoplay')) {
        activeVideo.play();
      }
    }
    
    // Make sure all video click handlers are set up correctly
    this.setupVideoClickHandlers();
  }
  
  setupMuteButtons() {
    const muteButtons = this.querySelectorAll('.mute-button');
    
    muteButtons.forEach(button => {
      const video = button.closest('.swiper-slide')?.querySelector('video');
      if (!video) return;
      
      // Set initial state
      this.updateMuteButtonState(button, video.muted);
      
      button.addEventListener('click', (e) => {
        // Stop event propagation to prevent triggering video play/pause
        e.stopPropagation();
        
        video.muted = !video.muted;
        this.updateMuteButtonState(button, video.muted);
      });
    });
  }
  
  updateMuteButtonState(button, isMuted) {
    if (isMuted) {
      button.classList.add('is-muted');
      button.classList.remove('is-unmuted');
      button.setAttribute('aria-label', 'Unmute video');
      button.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="1" y1="1" x2="23" y2="23"></line>
          <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
          <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path>
        </svg>
      `;
    } else {
      button.classList.add('is-unmuted');
      button.classList.remove('is-muted');
      button.setAttribute('aria-label', 'Mute video');
      button.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M11 5L6 9H2v6h4l5 4zM19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
        </svg>
      `;
    }
  }

  announceToScreenReader(message) {
    // Create an aria-live region for accessibility announcements
    let announcement = this.querySelector('.sr-announcement');
    if (!announcement) {
      announcement = document.createElement('div');
      announcement.className = 'sr-announcement';
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.style.position = 'absolute';
      announcement.style.width = '1px';
      announcement.style.height = '1px';
      announcement.style.overflow = 'hidden';
      announcement.style.clip = 'rect(0, 0, 0, 0)';
      this.appendChild(announcement);
    }
    announcement.textContent = message;
  }

  connectedCallback() {
    // Wait for DOM to be ready before initializing
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initSwiperSlider();
        this.setupMuteButtons();
        this.setupVideoClickHandlers();
      });
    } else {
      this.initSwiperSlider();
      this.setupMuteButtons();
      this.setupVideoClickHandlers();
    }
    
    // Add keyboard navigation support
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.swiper?.slidePrev();
      } else if (e.key === 'ArrowRight') {
        this.swiper?.slideNext();
      }
    });
  }
  
  setupVideoClickHandlers() {
    // Setup click handlers for all videos
    const videos = this.querySelectorAll('video');
    videos.forEach(video => {
      const videoContainer = video.closest('.video-container');
      const slide = video.closest('.carousel-slide');
      
      if (videoContainer && slide) {
        // Remove any existing click handlers
        videoContainer.onclick = null;
        
        // Add new click handler for play/pause toggle
        videoContainer.onclick = (e) => {
          // Don't trigger if clicking on mute button or its children (SVG, path, etc.)
          const muteButton = videoContainer.querySelector('.mute-button');
          if (muteButton && (muteButton === e.target || muteButton.contains(e.target))) {
            return;
          }
          
          // Check if this slide is active before allowing play/pause
          const isActiveSlide = slide.classList.contains('swiper-slide-active');
          if (!isActiveSlide) {
            // If not active, don't allow click interaction
            return;
          }
          
          if (video.paused) {
            video.play();
          } else {
            video.pause();
          }
        };
      }
    });
  }
}

customElements.define('your-routine-slider', YourRoutineSlider);

